import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Field } from "../components/Field";
import { PlatformMark } from "../components/PlatformMark";
import { ExtensionDevices } from "../components/ExtensionDevices";
import { signOut } from "../lib/auth";

interface SettingsShape {
  defaultPlatformFeePct: string;
  paymentProcessingPct: string;
  paymentProcessingFlat: string;
  avgPackagingCost: string;
  polyMailerCost: string;
  bubbleMailerCost: string;
  avgShippingCost: string;
  buyerPaysShipping: boolean;
  mileageRate: string;
  targetMarginPct: string;
  minAcceptableProfit: string;
}

interface ConnectedAccount {
  platform: string; isActive: boolean; lastError: string | null; expiresAt: string | null;
}

interface PlatformFee {
  platform: string;
  commissionPct: string; fixedFee: string;
  paymentPct: string; paymentFixed: string;
  lowPriceThreshold: string | null; lowPriceFee: string | null;
  sellerPaysShipping: boolean;
  boostPct: string;
}

const FEE_COLS: Array<[keyof PlatformFee, string]> = [
  ["commissionPct", "Commission %"],
  ["fixedFee", "Flat fee"],
  ["paymentPct", "Payment %"],
  ["paymentFixed", "Payment flat"],
  ["lowPriceThreshold", "Low-price <"],
  ["lowPriceFee", "Low-price fee"],
  ["boostPct", "Boost %"],
];

const NUMERIC_FIELDS: Array<[keyof SettingsShape, string, string]> = [
  ["defaultPlatformFeePct", "Platform fee %", "eBay final value fee runs about 13.25%"],
  ["paymentProcessingPct", "Processing %", "Charged on top of the platform fee"],
  ["paymentProcessingFlat", "Processing flat", "Per-order fixed charge"],
  ["polyMailerCost", "Poly mailer", "Charged once per parcel, not per item"],
  ["bubbleMailerCost", "Bubble mailer", "The pricier option, picked at mark-sold"],
  ["avgPackagingCost", "Other packaging", "Fallback for anything shipped another way"],
  ["avgShippingCost", "Shipping", "Used only when you cover postage"],
  ["mileageRate", "Mileage rate", "Per mile, for sourcing trips"],
  ["targetMarginPct", "Target margin %", "Drives the suggested asking price"],
  ["minAcceptableProfit", "Minimum profit", "Sets the floor price on every item"],
];

/**
 * Every margin in the app recomputes from these numbers. Change the fee here
 * and last quarter's profit figures correct themselves, because the ledger
 * derives fees rather than storing them.
 */
export function Settings() {
  const [settings, setSettings] = useState<SettingsShape | null>(null);
  const [accounts, setAccounts] = useState<ConnectedAccount[]>([]);
  const [fees, setFees] = useState<PlatformFee[]>([]);
  const [savedFee, setSavedFee] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const res = await api.get<{
          settings: SettingsShape; connectedAccounts: ConnectedAccount[];
          platformFees: PlatformFee[];
        }>("/settings");
        setSettings(res.settings);
        setAccounts(res.connectedAccounts);
        setFees(res.platformFees ?? []);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Could not load your settings");
      }
    })();
  }, []);

  function setFeeField(platform: string, key: keyof PlatformFee, value: string | boolean) {
    setFees((prev) => prev.map((f) => (f.platform === platform ? { ...f, [key]: value } : f)));
  }

  async function saveFee(fee: PlatformFee) {
    setError(null);
    try {
      const num = (v: string | null) => (v === null || v === "" ? null : Number(v));
      await api.patch(`/platform-fees/${fee.platform}`, {
        commissionPct: Number(fee.commissionPct),
        fixedFee: Number(fee.fixedFee),
        paymentPct: Number(fee.paymentPct),
        paymentFixed: Number(fee.paymentFixed),
        lowPriceThreshold: num(fee.lowPriceThreshold),
        lowPriceFee: num(fee.lowPriceFee),
        sellerPaysShipping: fee.sellerPaysShipping,
        boostPct: Number(fee.boostPct ?? 0),
      });
      setSavedFee(fee.platform);
      setTimeout(() => setSavedFee(null), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save that fee profile");
    }
  }

  async function save() {
    if (!settings) return;
    setError(null);
    try {
      const payload = Object.fromEntries(
        NUMERIC_FIELDS.map(([key]) => [key, Number(settings[key])])
      );
      await api.patch("/settings", { ...payload, buyerPaysShipping: settings.buyerPaysShipping });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save");
    }
  }

  if (!settings) {
    return <div className="shell">{error ? <div className="notice">{error}</div> : <p className="hint">Loading</p>}</div>;
  }

  return (
    <div className="shell">
      <header className="masthead">
        <span className="wordmark">settings</span>
      </header>

      {error && <div className="notice">{error}</div>}
      {saved && <div className="notice ok">Saved. Every margin figure recalculated.</div>}

      <section className="rail">
        <h2 className="eyebrow rail-label">Cost assumptions</h2>
        <div className="row">
          {NUMERIC_FIELDS.map(([key, label, hint]) => (
            <Field key={key} label={label} hint={hint}>
              <input
                className="money"
                type="number"
                step="0.01"
                min="0"
                value={settings[key] as string}
                onChange={(e) =>
                  setSettings({ ...settings, [key]: e.target.value } as SettingsShape)
                }
              />
            </Field>
          ))}
        </div>

        <label style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginTop: "0.5rem" }}>
          <input
            type="checkbox"
            style={{ width: "auto" }}
            checked={settings.buyerPaysShipping}
            onChange={(e) => setSettings({ ...settings, buyerPaysShipping: e.target.checked })}
          />
          <span>Buyer pays shipping</span>
        </label>

        <div className="actions">
          <button onClick={() => void save()}>Save changes</button>
        </div>
      </section>

      <section className="rail">
        <h2 className="eyebrow rail-label">Selling platform fees</h2>
        <p className="hint" style={{ marginTop: 0 }}>
          Each marketplace charges differently. These profiles drive the estimated profit
          shown on every item and next to each listing. Defaults reflect standard 2025–26 US
          rates — adjust to match your account.
        </p>
        {fees.length === 0 ? (
          <p className="hint">No fee profiles yet.</p>
        ) : (
          fees.map((f) => (
            <div key={f.platform} className="fee-card">
              <div className="fee-head">
                <span className="fee-name">{f.platform}</span>
                <button className="quiet" onClick={() => void saveFee(f)}>
                  {savedFee === f.platform ? "Saved ✓" : "Save"}
                </button>
              </div>
              <div className="fee-grid">
                {FEE_COLS.map(([key, label]) => (
                  <Field key={key} label={label}>
                    <input
                      className="money"
                      type="number"
                      step="0.01"
                      min="0"
                      value={(f[key] as string | null) ?? ""}
                      onChange={(e) => setFeeField(f.platform, key, e.target.value)}
                    />
                  </Field>
                ))}
              </div>
              <label className="fee-ship">
                <input
                  type="checkbox"
                  style={{ width: "auto" }}
                  checked={f.sellerPaysShipping}
                  onChange={(e) => setFeeField(f.platform, "sellerPaysShipping", e.target.checked)}
                />
                <span>Seller pays shipping on {f.platform}</span>
              </label>
            </div>
          ))
        )}
      </section>

      <ExtensionDevices />

      <section className="rail">
        <h2 className="eyebrow rail-label">Connected accounts</h2>
        {accounts.length === 0 ? (
          <div className="empty">
            <p>No marketplaces connected. eBay is the one that supports full automation.</p>
          </div>
        ) : (
          accounts.map((a) => (
            <div key={a.platform} className="tag" style={{ cursor: "default" }}>
              <PlatformMark platform={a.platform} />
              <span className="tag-body">
                <span className="tag-title">{a.platform}</span>
                <span className="tag-meta">
                  {a.isActive ? "Connected" : "Disconnected"}
                  {a.lastError && <span style={{ color: "var(--tag-alert)" }}>{a.lastError}</span>}
                </span>
              </span>
            </div>
          ))
        )}
      </section>

      <div className="actions">
        <button className="quiet" onClick={() => { signOut(); window.location.reload(); }}>
          Sign out
        </button>
      </div>
    </div>
  );
}
